import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Inbox, ArrowRightLeft, CheckCircle2, XCircle, FileText, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TransferInbox() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [inbox, setInbox] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [summary, setSummary] = useState(null);
  const [handover, setHandover] = useState({}); // transferId -> text

  const load = async () => {
    try {
      const [i, o] = await Promise.all([
        axios.get(`${API}/api/transfer/inbox`, { withCredentials: true }),
        axios.get(`${API}/api/transfer/outgoing`, { withCredentials: true }),
      ]);
      setInbox(i.data.items || []); setOutgoing(o.data.items || []);
    } catch { toast.error("Couldn't load."); }
  };
  useEffect(() => { load(); }, []);

  const openSummary = async (t) => {
    setExpanded(t.id); setSummary(null);
    try {
      const { data } = await axios.get(`${API}/api/transfer/${t.id}/summary`, { withCredentials: true });
      setSummary(data.summary);
    } catch { toast.error("Couldn't load summary."); }
  };
  const decide = async (id, accept) => {
    try {
      await axios.post(`${API}/api/transfer/${id}/decide`, { accept }, { withCredentials: true });
      toast.success(accept ? "Learner linked to you." : "Declined.");
      setExpanded(null); setSummary(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't decide."); }
  };
  const submitHandover = async (id) => {
    const note = (handover[id] || "").trim();
    if (!note) { toast.error("Write your handover note first."); return; }
    try {
      await axios.post(`${API}/api/transfer/${id}/handover`, { note }, { withCredentials: true });
      toast.success("Handover saved to their passport.");
      setHandover({ ...handover, [id]: "" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="transfer-inbox-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
          <h1 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5"><ArrowRightLeft className="w-4 h-4" /> Transfers</h1>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Inbox */}
        <section>
          <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><Inbox className="w-3 h-3" /> Incoming transfer requests</p>
          {inbox.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-6">No pending requests.</p>
          ) : (
            <ul data-testid="transfer-inbox-list" className="space-y-2">
              {inbox.map((t) => (
                <li key={t.id} data-testid={`transfer-${t.id}`} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 flex items-center justify-center text-xs font-extrabold">{(t.learner_name || "L").charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-slate-900 dark:text-white">{t.learner_name || "Learner"}</p>
                      {t.reason && <p className="text-[11px] text-slate-500 italic">"{t.reason}"</p>}
                    </div>
                  </div>
                  {expanded === t.id ? (
                    summary ? (
                      <div data-testid="transfer-summary" className="rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3 space-y-1 text-xs">
                        {summary.headline && summary.headline.map((h) => (
                          <p key={h} className="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
                            <Sparkles className="w-3 h-3 mt-0.5 text-indigo-500" /> {h}
                          </p>
                        ))}
                        {summary.strengths?.length > 0 && <p><span className="font-bold">Strengths:</span> {summary.strengths.join(" · ")}</p>}
                        {summary.priority_gaps?.length > 0 && <p><span className="font-bold">Priority gaps:</span> {summary.priority_gaps.join(" · ")}</p>}
                        {summary.preferences?.nervous_triggers?.length > 0 && <p><span className="font-bold">Nervous on:</span> {summary.preferences.nervous_triggers.join(" · ")}</p>}
                        {summary.preferences?.reasonable_adjustments && <p><span className="font-bold">Adjustments:</span> {summary.preferences.reasonable_adjustments}</p>}
                        {summary.latest_handover_note && <p className="rounded bg-purple-100 dark:bg-purple-900/30 p-2 text-purple-900 dark:text-purple-200"><span className="font-bold">Prev. handover:</span> {summary.latest_handover_note.text}</p>}
                      </div>
                    ) : <p className="text-xs text-slate-400">Loading summary…</p>
                  ) : (
                    <button data-testid={`transfer-view-${t.id}`} onClick={() => openSummary(t)} className="w-full text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 flex items-center justify-center gap-1"><FileText className="w-3 h-3" /> View Transfer Ready summary</button>
                  )}
                  {expanded === t.id && (
                    <div className="flex gap-2">
                      <button data-testid={`transfer-accept-${t.id}`} onClick={() => decide(t.id, true)} className="flex-1 text-xs font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 flex items-center justify-center gap-1"><CheckCircle2 className="w-3 h-3" /> Accept</button>
                      <button data-testid={`transfer-decline-${t.id}`} onClick={() => decide(t.id, false)} className="flex-1 text-xs font-bold px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1"><XCircle className="w-3 h-3" /> Decline</button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Outgoing — awaiting handover */}
        <section>
          <p className="text-xs font-extrabold uppercase tracking-wider text-slate-500 mb-2">Outgoing — handover needed</p>
          {outgoing.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No pending handovers.</p>
          ) : (
            <ul data-testid="transfer-outgoing-list" className="space-y-2">
              {outgoing.map((t) => (
                <li key={t.id} data-testid={`outgoing-${t.id}`} className="rounded-2xl bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-900/40 p-3 space-y-2">
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">{t.learner_name} is moving to {t.to_instructor_name}</p>
                  <p className="text-[11px] text-purple-800 dark:text-purple-300">What's the one thing the next instructor should know?</p>
                  <textarea data-testid={`outgoing-text-${t.id}`} value={handover[t.id] || ""} onChange={(e) => setHandover({ ...handover, [t.id]: e.target.value })} rows={3} maxLength={800}
                    placeholder="Keep it factual. e.g. 'Confident on junctions; needs a calm tone on busy roundabouts.'"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-purple-200 dark:border-purple-900/40 bg-white dark:bg-slate-900 dark:text-white resize-none" />
                  <button data-testid={`outgoing-save-${t.id}`} onClick={() => submitHandover(t.id)} className="w-full text-xs font-bold px-3 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500">Send handover</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
